import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@carbon/react';
import { showToast } from '@/redux/new_services/toasts';
import {
  TextBold,
  TextItalic,
  TextStrikethrough,
  Code,
  ListBulleted,
  ListNumbered,
  Quotes,
  Link as LinkIcon,
  Close as UnlinkIcon,
  Image as ImageIcon,
  Video as VideoIcon,
  Table as TableIcon,
  TableSplit,
  RowDelete,
  Undo,
  Redo,
} from '@carbon/react/icons';

// Single toolbar button that toggles its `is-active` class based on the
// editor's current mark/node state.
const TbBtn = ({ active, disabled, onClick, label, children }) => (
  <Button
    kind="ghost"
    size="sm"
    hasIconOnly
    iconDescription={label}
    tooltipPosition="bottom"
    onClick={onClick}
    disabled={disabled}
    className={`rte-toolbar__btn${active ? ' rte-toolbar__btn--active' : ''}`}
    renderIcon={() => children}
  />
);

const Divider = () => <div className="rte-toolbar__divider" />;

// EditorToolbar is the floating toolbar above the Tiptap content area.
// Mostly thin wrappers around editor.chain() so the editor remains the
// single source of truth.
const EditorToolbar = ({ editor, onUploadImage, onInsertImageByUrl, onUploadVideo }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  if (!editor) {
    return <div className="rte-toolbar rte-toolbar--placeholder" />;
  }

  const can = editor.can();

  const promptLink = () => {
    const previous = editor.getAttributes('link').href || '';
    const url = window.prompt('Link-URL eingeben (leer lassen zum Entfernen):', previous);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleImage = () => {
    if (onUploadImage) {
      fileInputRef.current?.click();
      return;
    }
    const url = window.prompt('Bild-URL eingeben:');
    if (url) onInsertImageByUrl?.(url);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onUploadImage?.(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleVideo = () => {
    if (!onUploadVideo) return;
    videoInputRef.current?.click();
  };

  // Video upload + transcode can take a while — gate the toolbar button so
  // the operator sees a clear "busy" state and we don't fire two uploads
  // at once if they click impatiently. Errors are surfaced as a toast
  // because the uploadHelper rejects with the conversion status from the
  // worker; without a catch the rejection ended up as an unhandled promise
  // and the user got no feedback at all. The caption is asked up front so
  // the user has a moment to type something while ffmpeg transcodes in
  // the background.
  const handleVideoFile = async (e) => {
    const file = e.target.files?.[0];
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (!file) return;
    const caption = window.prompt(
      'Bildunterschrift für das Video (optional, kann leer bleiben):',
      '',
    );
    // null === user pressed Cancel → bail out completely, don't upload
    if (caption === null) return;
    try {
      setIsUploadingVideo(true);
      await onUploadVideo?.(file, caption);
    } catch (err) {
      const message =
        (err && (err.message || err.toString())) || 'Unbekannter Fehler';
      dispatch(
        showToast({
          kind: 'error',
          title: 'Video-Upload fehlgeschlagen',
          caption: message,
        }),
      );
    } finally {
      setIsUploadingVideo(false);
    }
  };

  return (
    <div className="rte-toolbar">
      <TbBtn
        label="Fett"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <TextBold size={16} />
      </TbBtn>
      <TbBtn
        label="Kursiv"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <TextItalic size={16} />
      </TbBtn>
      <TbBtn
        label="Durchgestrichen"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <TextStrikethrough size={16} />
      </TbBtn>
      <TbBtn
        label="Inline-Code"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code size={16} />
      </TbBtn>

      <Divider />

      <Button
        kind="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`rte-toolbar__btn${editor.isActive('heading', { level: 1 }) ? ' rte-toolbar__btn--active' : ''}`}
      >
        H1
      </Button>
      <Button
        kind="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`rte-toolbar__btn${editor.isActive('heading', { level: 2 }) ? ' rte-toolbar__btn--active' : ''}`}
      >
        H2
      </Button>
      <Button
        kind="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`rte-toolbar__btn${editor.isActive('heading', { level: 3 }) ? ' rte-toolbar__btn--active' : ''}`}
      >
        H3
      </Button>
      <Button
        kind="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setParagraph().run()}
        className={`rte-toolbar__btn${editor.isActive('paragraph') ? ' rte-toolbar__btn--active' : ''}`}
      >
        ¶
      </Button>

      <Divider />

      <TbBtn
        label="Aufzählung"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListBulleted size={16} />
      </TbBtn>
      <TbBtn
        label="Nummerierte Liste"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListNumbered size={16} />
      </TbBtn>
      <TbBtn
        label="Zitat"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quotes size={16} />
      </TbBtn>

      <Divider />

      <TbBtn
        label="Link"
        active={editor.isActive('link')}
        onClick={promptLink}
      >
        <LinkIcon size={16} />
      </TbBtn>
      <TbBtn
        label="Link entfernen"
        disabled={!editor.isActive('link')}
        onClick={() => editor.chain().focus().unsetLink().run()}
      >
        <UnlinkIcon size={16} />
      </TbBtn>

      <TbBtn label="Bild einfügen" onClick={handleImage}>
        <ImageIcon size={16} />
      </TbBtn>
      {onUploadImage && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      )}

      {onUploadVideo && (
        <>
          <TbBtn
            label={isUploadingVideo ? 'Video wird hochgeladen …' : 'Video einfügen'}
            disabled={isUploadingVideo}
            onClick={handleVideo}
          >
            <VideoIcon size={16} />
          </TbBtn>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            style={{ display: 'none' }}
            onChange={handleVideoFile}
          />
        </>
      )}

      <Divider />

      <TbBtn
        label="Tabelle einfügen (3×3)"
        onClick={() =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run()
        }
      >
        <TableIcon size={16} />
      </TbBtn>
      <TbBtn
        label="Spalte hinzufügen"
        disabled={!editor.isActive('table')}
        onClick={() => editor.chain().focus().addColumnAfter().run()}
      >
        <TableSplit size={16} />
      </TbBtn>
      <TbBtn
        label="Tabelle entfernen"
        disabled={!editor.isActive('table')}
        onClick={() => editor.chain().focus().deleteTable().run()}
      >
        <RowDelete size={16} />
      </TbBtn>

      <Divider />

      <TbBtn
        label="Rückgängig"
        disabled={!can.undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo size={16} />
      </TbBtn>
      <TbBtn
        label="Wiederholen"
        disabled={!can.redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo size={16} />
      </TbBtn>
    </div>
  );
};

export default EditorToolbar;
